import PaginationWithState from "components/Pagination/PaginationWithState";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import BulkUpdateStatus from "pages/Sales/UpsTracking/BulkUpdateStatus";
import { CourierCell, trackingUrlFor } from "pages/Sales/UpsTracking/courier";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import exportPkShipments from "./pkShipmentsExport";

/**
 * Manufacturing -> Utilities -> PK Shipments (user, 2026-10-30): the
 * Shipments rows on the PK-DHL and PK-UPS couriers, as a report. View,
 * update tracking (Bulk Update Status, or one row's refresh button) and
 * Export, like the old DHL Tracking module. No adding or editing: that is
 * Sales -> Shipments.
 */

const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "in-transit", label: "In-Transit" },
  { value: "delivered", label: "Delivered" },
];
const LIMIT = 20;

export default function PkShipments() {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  // What the list shows; the search box only applies on Search / Enter.
  const [filters, setFilters] = useState({ status: "all", courierId: "", query: "", startDate: "", endDate: "" });
  const [searchText, setSearchText] = useState("");

  const notify = useCallback(
    (message, type = "success") => dispatch(setNotification({ message, type })),
    [dispatch],
  );
  // The request body: the To day runs to its last second.
  const body = useCallback(
    () => ({ ...filters, endDate: filters.endDate ? `${filters.endDate} 23:59:59` : "" }),
    [filters],
  );
  const setFilter = (patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.post(`/pk-shipments?page=${page}&limit=${LIMIT}`, body(), {
        headers: authHeader(),
      });
      if (!data?.success) throw new Error(data?.message || "Failed");
      setRows(data.data || []);
      setCouriers(data.couriers || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
      setLastUpdated(data.lastUpdated || null);
    } catch (err) {
      notify(err?.response?.data?.message || err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [page, body, notify]);

  useEffect(() => {
    load();
  }, [load]);

  // One row's refresh button: its latest status from the courier.
  const updateOne = async (item) => {
    try {
      setUpdatingId(item.id);
      const { data } = await API.post(
        "/pk-shipments/update-tracking",
        { ids: [item.id] },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message || "Failed");
      if (data.updated) notify(`${item.trackingNumber} updated`);
      else notify(data.errors?.[0]?.message || "No tracking update available", "error");
      await load();
    } catch (err) {
      notify(err?.response?.data?.message || err.message, "error");
    } finally {
      setUpdatingId(null);
    }
  };

  // Every row under the current filters, as the DHL module's sheet.
  const exportAll = async () => {
    try {
      setExporting(true);
      const { data } = await API.post("/pk-shipments/export", body(), { headers: authHeader() });
      if (!data?.success) throw new Error(data?.message || "Failed");
      if (!data.data?.length) {
        notify("No data to export", "error");
        return;
      }
      exportPkShipments(data.data);
    } catch (err) {
      notify(err?.response?.data?.message || err.message, "error");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-2" style={{ marginLeft: 260, height: "100vh" }}>
      {loading && <Spinner />}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3 ps-2">
        <h2 className="mb-0 me-auto">PK Shipments</h2>
        <BulkUpdateStatus lastUpdated={lastUpdated} onDone={load} url="/pk-shipments/update-tracking" />
        <button type="button" className="btn btn-outline-success" disabled={exporting} onClick={exportAll}>
          <i className={`mdi ${exporting ? "mdi-loading mdi-spin" : "mdi-file-excel"} me-1`}></i>
          Export
        </button>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3 px-2">
        <div className="btn-group" role="group" aria-label="Status">
          {STATUS_TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              className={`btn ${filters.status === t.value ? "btn-primary" : "bg-white"}`}
              onClick={() => setFilter({ status: t.value })}
            >
              {t.label}
            </button>
          ))}
        </div>
        <select
          className="form-select w-auto"
          aria-label="Courier"
          value={filters.courierId}
          onChange={(e) => setFilter({ courierId: e.target.value })}
        >
          <option value="">All Couriers</option>
          {couriers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <input
          type="date"
          className="form-control w-auto"
          aria-label="From"
          value={filters.startDate}
          onChange={(e) => setFilter({ startDate: e.target.value })}
        />
        <span className="text-muted">to</span>
        <input
          type="date"
          className="form-control w-auto"
          aria-label="To"
          value={filters.endDate}
          onChange={(e) => setFilter({ endDate: e.target.value })}
        />
        <form
          className="d-flex gap-1 ms-auto"
          onSubmit={(e) => {
            e.preventDefault();
            setFilter({ query: searchText.trim() });
          }}
        >
          <input
            className="form-control"
            placeholder="Search tracking #, customer..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <button type="submit" className="btn btn-info">
            Search
          </button>
          {(filters.query || filters.startDate || filters.endDate || filters.courierId) && (
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={() => {
                setSearchText("");
                setFilter({ courierId: "", query: "", startDate: "", endDate: "" });
              }}
            >
              Clear
            </button>
          )}
        </form>
      </div>

      <div className="px-2" style={{ height: "72vh", overflow: "auto" }}>
        <table className="table table-sm table-hover align-middle bg-white">
          <thead className="sticky-top bg-white">
            <tr>
              <th>Ship Date</th>
              <th>Courier</th>
              <th>Tracking #</th>
              <th>C-ID</th>
              <th>Customer</th>
              <th>Location</th>
              <th>Status</th>
              <th>Tracking Details</th>
              <th>Last Updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => (
              <tr key={item.id}>
                <td className="text-nowrap">
                  {item.shipDate ? new Date(item.shipDate).toLocaleDateString() : "-"}
                </td>
                <td>
                  <CourierCell courier={item.courier} />
                </td>
                <td>
                  <a href={trackingUrlFor(item)} target="_blank" rel="noreferrer">
                    {item.trackingNumber}
                  </a>
                </td>
                <td>{item.customerId ? `C-${item.customerId}` : "-"}</td>
                <td>{item.customer?.customerName || <span className="text-muted">{item.customerName || "-"}</span>}</td>
                <td>{item.location?.locationName || "-"}</td>
                <td>
                  <span className={`badge ${item.status === "delivered" ? "bg-success" : "bg-warning text-dark"}`}>
                    {item.status === "delivered" ? "Delivered" : "In-Transit"}
                  </span>
                </td>
                <td className="small" style={{ maxWidth: 320 }}>
                  {item.trackingInfo || "-"}
                </td>
                <td className="small text-nowrap">
                  {item.lastUpdated ? new Date(item.lastUpdated).toLocaleString() : "-"}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-info"
                    title="Update tracking"
                    aria-label={`Update tracking of ${item.trackingNumber}`}
                    disabled={updatingId === item.id || !item.trackingNumber}
                    onClick={() => updateOne(item)}
                  >
                    <i className={`mdi ${updatingId === item.id ? "mdi-loading mdi-spin" : "mdi-refresh"}`}></i>
                  </button>
                </td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr>
                <td colSpan={10} className="text-center text-muted py-4">
                  {couriers.length
                    ? "No shipments."
                    : "No PK-DHL or PK-UPS courier in Settings -> Couriers."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="d-flex justify-content-between align-items-center px-2 mt-2">
        <small className="text-muted">{total} shipments</small>
        <PaginationWithState totalPages={totalPages} currPage={page} setCurrPage={setPage} />
      </div>
    </div>
  );
}
